import { Box, Button, Grid, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import ReactQuill from 'react-quill';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, Language, UpdateBusinessDetailsInput } from '../../../api';
import 'react-quill/dist/quill.snow.css';

type AboutTabProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

const quillModules = {
  toolbar: [['bold'], [{ list: 'bullet' }], ['link']],
};

export const AboutTab = ({ account, updateLocalAccount, setIsUpdatedSnackOpen }: AboutTabProps) => {
  const languages = account.customer_facing_widgets_languages;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(languages[0]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: Language) => {
    setActiveTab(newValue);
  };

  const initialValues = Object.fromEntries(
    languages.map((lang) => [lang, account?.business_details?.about_content?.[lang] || '']),
  ) as Record<Language, string>;

  const onSubmit = async (values: typeof initialValues) => {
    setIsUpdating(true);
    const input: UpdateBusinessDetailsInput = {
      about_content: values,
    };
    const updatedAccount = await AccountService.updateBusinessDetails(input);
    await updateLocalAccount(updatedAccount);
    setIsUpdating(false);
    setIsUpdatedSnackOpen(true);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleSubmit, values, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column">
            <Grid container flexDirection="column" flex={1}>
              <Grid item mb={2}>
                <Typography variant="h4">{t('business_details.about')}</Typography>
              </Grid>
              <Grid item mb={2}>
                <Typography variant="body1" color="text.secondary">
                  {t('business_details.note')}
                </Typography>
              </Grid>
              {languages.length > 1 && (
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="language tabs" sx={{ mb: 2 }}>
                  {languages.map((lang) => (
                    <Tab key={lang} label={t(`emailTemplates.${lang}`)} value={lang} />
                  ))}
                </Tabs>
              )}
              <Box
                key={activeTab}
                sx={{
                  '& .ql-editor': {
                    direction: activeTab !== Language.HE ? 'rtl !important' : 'ltr',
                    textAlign: activeTab !== Language.HE ? 'right !important' : 'left',
                    maxHeight: isSmallScreen ? '400px' : '600px',
                    overflow: 'auto',
                  },
                }}>
                <ReactQuill
                  theme="snow"
                  value={values[activeTab] || ''}
                  onChange={(content) => setFieldValue(activeTab, content)}
                  modules={quillModules}
                  readOnly={isUpdating}
                />
              </Box>
            </Grid>
            <Grid container justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" disabled={isUpdating}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
